// src/components/Auditlog/Auditlog.jsx
import React, { useEffect, useState, useCallback } from 'react';
import Menu from '../Menu/Menu';
import { useAuth } from '../../auth/AuthContext';
import api from '../../utils/axios.secure';
import './css/auditlog.css';

// // ISO 문자열 → "YYYY-MM-DD HH:mm:ss" (KST)로 변환
// const formatDateTime = (value) => {
//   if (!value) return '';
//   const d = new Date(value);
//   if (Number.isNaN(d.getTime())) return String(value);

//   // KST 보정 (서버가 UTC라면)
//   const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);

//   const yyyy = kst.getFullYear();
//   const mm = String(kst.getMonth() + 1).padStart(2, '0');
//   const dd = String(kst.getDate()).padStart(2, '0');
//   const hh = String(kst.getHours()).padStart(2, '0');
//   const mi = String(kst.getMinutes()).padStart(2, '0');
//   const ss = String(kst.getSeconds()).padStart(2, '0');

//   return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
// };

// ISO 문자열 → "YYYY-MM-DD HH:mm:ss" (KST)로 변환
const formatDateTime = (value) => {
  if (!value) return '';

  // ✅ DB가 이미 "YYYY-MM-DD HH:mm:ss" 형태로 주면 그대로 보여주기
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}$/.test(value)) {
    return value; // 이미 KST라고 보면 됨
  }

  // 그 외(ISO 등)만 파싱해서 출력
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const ManagerAuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const [from, setFrom] = useState(''); // YYYY-MM-DD
  const [to, setTo] = useState('');

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);

  const fetchLogs = useCallback(
    async (pageOverride) => {
      const currentPage = pageOverride || page;

      setLoading(true);
      setErrorMsg('');

      try {
        const body = {
          page: currentPage,
          pageSize,
          from: from || undefined,
          to: to || undefined,
        };

        const res = await api.post('/manager/audit/selectAuditLogs', body);

        // ✅ sendResponseCode 구조에 맞게 아래 부분 한 번만 확인해줘
        // 예시 1) { code, message, data: { data, totalCount, page, pageSize } }
        // 예시 2) { code, message, resultData, resultMap }
        // 콘솔 찍어보고 필요하면 아래 매핑만 수정하면 됨.
        // console.log('audit logs response:', res.data);

        const payload =
          res.data.data ||       // 케이스 1
          res.data.resultData || // 케이스 2
          {};

        setLogs(payload.data || payload.items || []);
        setTotalCount(payload.totalCount || 0);
        setPage(payload.page || currentPage);
        setPageSize(payload.pageSize || pageSize);
      } catch (err) {
        console.error('감사로그 조회 실패:', err);
        setErrorMsg('감사로그를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, from, to]
  );

  // 처음 진입 시 1페이지 조회
  useEffect(() => {
    fetchLogs(1);
  }, [pageSize]); // pageSize 바뀌면 1페이지부터

  const handleSearch = () => {
    // 검색 버튼 → 항상 1페이지부터
    fetchLogs(1);
  };

  const handlePrev = () => {
    if (page <= 1) return;
    fetchLogs(page - 1);
  };

  const handleNext = () => {
    if (page >= totalPages) return;
    fetchLogs(page + 1);
  };

  return (
    <Menu>
      <div className="audit-page">
        <h2>감사로그 조회</h2>

        {/* 검색 영역 */}
        <div className="audit-filters" style={{ marginBottom: '16px' }}>
          <label style={{ marginRight: '8px' }}>
            시작일:
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ marginLeft: '4px' }}
            />
          </label>

          <label style={{ marginRight: '8px' }}>
            종료일:
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ marginLeft: '4px' }}
            />
          </label>

          <label style={{ marginRight: '8px' }}>
            페이지 크기:
            <select
              value={pageSize}
              onChange={(e) => {
                const v = Number(e.target.value);
                setPageSize(v);
              }}
              style={{ marginLeft: '4px' }}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>

          <button type="button" onClick={handleSearch}>
            검색
          </button>
        </div>

        {/* 상태 메시지 */}
        {loading && <div>로딩 중...</div>}
        {errorMsg && (
          <div style={{ color: 'red', marginBottom: '8px' }}>{errorMsg}</div>
        )}

        {/* 결과 테이블 */}
        <table className="audit-table">
          <thead>
            <tr>
              <th>일시</th>
              <th>행위자 유형</th>
              <th>행위자 ID</th>
              <th>채널</th>
              <th>액션</th>
              <th>대상 타입</th>
              <th>대상 ID</th>
              <th>결과</th>
              <th>사유</th>
              <th>IP</th>
              <th>User Agent</th>      {/* ← 추가됨 */}
              <th>Correlation ID</th>
              <th>변경 요약</th>
            </tr>
          </thead>

          <tbody>
            {logs.map((log) => (
              <tr key={log.idx}>
                <td>{formatDateTime(log.created_at)}</td>
                <td>{log.actor_type}</td>
                <td>{log.actor_id}</td>
                <td>{log.source}</td>
                <td>{log.action}</td>
                <td>{log.target_type}</td>
                <td>{log.target_id}</td>
                <td>{log.outcome}</td>
                <td>{log.reason}</td>
                <td>{log.ip}</td>
                <td>{log.user_agent}</td>  {/* ← 추가됨 */}
                <td>{log.correlation_id}</td>
                <td>{log.change_summary || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 페이지네이션 */}
        <div
          className="audit-pagination"
          style={{
            marginTop: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            총 {totalCount}건 / {page} / {totalPages} 페이지
          </div>
          <div>
            <button type="button" onClick={handlePrev} disabled={page <= 1}>
              이전
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={page >= totalPages}
              style={{ marginLeft: '8px' }}
            >
              다음
            </button>
          </div>
        </div>
      </div>
    </Menu>
  );
};

export default ManagerAuditLog;